import { ChakraProvider } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "./App.css";
import { Home } from "./Home/Home";
import { RecoilRoot } from "recoil";
import { About } from "./About/About";
import { Chamois } from "./Chamois/Chamios";
import Video from "./Home/webreel.mp4";
import { Inquire } from "./Inquire/Inquire";
import { RedDeer } from "./RedDeer/RedDeer";
import { Team } from "./Team/Team";
import { Thar } from "./Thar/Thar";
import { TrophyHunting } from "./TrophyHunting/TrophyHunting";
import { Packages } from "./Packages/Packages";
import { AiOutlineCopyright } from "react-icons/ai";
import { OutReach } from "./OutReach/OutReach";
import redDeer from "./RedDeer/Stag.jpg";
import { Goat } from "./Goat/Goat";
import { Fallow } from "./Fallow/Fallow";
import { Rams } from "./Rams/Rams";
import { Helmet } from "react-helmet";

function App() {
  const [loading, setLoading] = useState(true);

  const vid = useRef<HTMLVideoElement | null>(null);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "NZ COUNTRY - Guided Hunting in New Zealand",
    image:
      "https://firebasestorage.googleapis.com/v0/b/nzcountry.appspot.com/o/images%2FThar%20Kill_3.37.1.jpg?alt=media&token=b8b68201-3366-4185-930b-465dc2e9f1c2",
    description:
      "Looking for a guided hunting experience in New Zealand? With plenty of options for a New Zealand adventure, NZ COUNTRY offers guided hunting for tahr, red deer, chamois, goat, fallow deer, and Arapawa rams. Experience New Zealand's high alpine terrain, amazing wildlife, and breathtaking scenery on a hunt with us.",
    brand: {
      "@type": "Brand",
      name: "NZ COUNTRY",
    },
    url: "https://www.nzcountry.guide",
    areaServed: {
      "@type": "Country",
      name: "New Zealand",
    },
    makesOffer: [
      {
        "@type": "Offer",
        name: "Guided Tahr Hunt",
        itemOffered: {
          "@type": "Service",
          name: "Guided Tahr Hunting Expedition",
          serviceType: "Guided Hunting",
          description:
            "Experience a challenging and rewarding guided hunt for Himalayan Tahr in New Zealand's spectacular Southern Alps.",
          category: "Big Game Hunting",
          areaServed: {
            "@type": "Place",
            name: "Southern Alps, New Zealand",
          },
          keywords:
            "Tahr hunting, guided tahr hunt, New Zealand big game, alpine hunting, Himalayan Tahr, helicopter flight, trophy tahr",
        },
        availability: "https://schema.org/InStock", // Or specific dates/seasons
        priceCurrency: "USD", // Optional: if you list specific prices
      },
      {
        "@type": "Offer",
        name: "Guided Chamois Hunt",
        itemOffered: {
          "@type": "Service",
          name: "Guided Chamois Hunting Adventure",
          serviceType: "Guided Hunting",
          description:
            "Pursue the agile and elusive Chamois with experienced guides in New Zealand's rugged high-country.",
          category: "Big Game Hunting",
          areaServed: {
            "@type": "Place",
            name: "High Country, South Island, New Zealand",
          },
          keywords:
            "Chamois hunting, guided chamois hunt, New Zealand alpine hunting, trophy chamois",
        },
        availability: "https://schema.org/InStock",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        name: "Guided Goat Hunt",
        itemOffered: {
          "@type": "Service",
          name: "Guided Goat Hunting Trip",
          serviceType: "Guided Hunting",
          description:
            "Enjoy a thrilling guided goat hunt, targeting feral goats in diverse New Zealand terrain. Suitable for all experience levels.",
          category: "Hunting",
          areaServed: {
            "@type": "Place",
            name: "Various Locations, New Zealand",
          },
          keywords:
            "Goat hunting, guided goat hunt, feral goat, New Zealand hunting, trophy goat",
        },
        availability: "https://schema.org/InStock",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        name: "Guided Arapawa Ram Hunt",
        itemOffered: {
          "@type": "Service",
          name: "Guided Arapawa Ram Hunting",
          serviceType: "Guided Hunting",
          description:
            "Hunt the unique and historic Arapawa Ram with our specialized guided services in New Zealand.",
          category: "Specialty Hunting",
          areaServed: {
            "@type": "Place",
            name: "Select locations, South Island, New Zealand",
          },
          keywords:
            "Arapawa Ram hunting, guided ram hunt, New Zealand heritage breed, trophy ram",
        },
        availability: "https://schema.org/InStock",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        name: "Guided Red Deer Hunt",
        itemOffered: {
          "@type": "Service",
          name: "Guided Red Deer Stag Hunt",
          serviceType: "Guided Hunting",
          description:
            "Track and hunt magnificent Red Stags with our expert guides in prime New Zealand deer habitat, especially during the roar.",
          category: "Big Game Hunting",
          areaServed: {
            "@type": "Place",
            name: "Prime Deer Habitat, New Zealand", // e.g., Fiordland, West Coast, private estates
          },
          keywords:
            "Red Deer hunting, guided stag hunt, New Zealand red stag, the roar, trophy deer, Douglas score, SCI score, trophy stag",
        },
        availability: "https://schema.org/InStock",
        priceCurrency: "USD",
      },
      {
        "@type": "Offer",
        name: "Guided Fallow Deer Hunt",
        itemOffered: {
          "@type": "Service",
          name: "Guided Fallow Buck Hunt",
          serviceType: "Guided Hunting",
          description:
            "Pursue impressive Fallow Bucks with our guided hunts in select New Zealand locations known for quality Fallow Deer.",
          category: "Big Game Hunting",
          areaServed: {
            "@type": "Place",
            name: "West Coast, South Island, New Zealand",
          },
          keywords:
            "Fallow Deer hunting, guided fallow buck hunt, New Zealand fallow deer, trophy buck",
        },
        availability: "https://schema.org/InStock",
        priceCurrency: "USD",
      },
    ],
  };

  useEffect(() => {
    if (!vid.current) return;
    vid.current.play();
  }, []);
  return (
    <RecoilRoot>
      <ChakraProvider>
        <BrowserRouter>
          <div>
            <Helmet>
              <title>
                Guided Hunting New Zealand | Tahr, Chamois, Deer Hunts
              </title>
              <meta
                name="description"
                content="Book your guided hunting trip in New Zealand for Tahr, Chamois, Red Deer, Fallow Deer and more with NZ COUNTRY."
              />
              <script type="application/ld+json">
                {JSON.stringify(schema)}
              </script>
            </Helmet>
            <div
              style={{ height: "100vh", width: "100vw", overflow: "hidden" }}
            >
              {loading && (
                <div
                  style={{
                    position: "absolute",
                    top: "0px",
                    zIndex: "11",
                    left: "0px",
                    height: "100vh",
                    width: "100vw",
                    overflowY: "hidden",
                    backgroundImage: `url(${redDeer})`,
                    backgroundPosition: "center",
                  }}
                ></div>
              )}
              <div
                style={{
                  position: "absolute",
                  top: "0px",
                  zIndex: "10",
                  left: "0px",
                  height: "100vh",
                  width: "100vw",
                  overflowY: "hidden",
                }}
              >
                <video
                  id="bannerVideo"
                  ref={vid}
                  autoPlay
                  muted
                  loop
                  style={{
                    objectFit: "cover",
                    height: "100vh",
                    width: "100vw",
                  }}
                  onLoadStart={() => setLoading(true)}
                  onLoadedData={(e) => {
                    setLoading(false);
                  }}
                >
                  <source src={Video} type="video/mp4" />
                </video>
              </div>
              {loading && (
                <div
                  style={{
                    position: "absolute",
                    top: "80vh",
                    zIndex: "10",
                    left: `${window.innerWidth / 2 - 30}px`,
                    textAlign: "center",
                    verticalAlign: "center",
                  }}
                >
                  LOADING...
                </div>
              )}
              <div
                style={{
                  position: "absolute",
                  top: "0px",
                  zIndex: "15",
                  left: "0px",
                  height: "100vh",
                  width: "100vw",
                  overflowY: "hidden",
                  background:
                    "linear-gradient(0deg, rgba(23,23,23,1) 0%, rgba(0,0,0,0.5298494397759104) 30%, rgba(0,0,0,0) 60%)",
                  filter:
                    'progid:DXImageTransform.Microsoft.gradient(startColorstr="#171717",endColorstr="#000000",GradientType=1)',
                }}
              >
                <Routes>
                  <Route path="/" element={<Home />} />
                  {/* <Route path="/gallery" element={<Gallery />} /> */}
                  <Route path="/trophy-hunting" element={<TrophyHunting />} />
                  <Route
                    path="/trophy-hunting-new-zealand"
                    element={<TrophyHunting />}
                  />
                  <Route path="/red-deer-trophy-hunting-new-zealand" element={<RedDeer />} />
                  <Route path="/red-deer" element={<RedDeer />} />
                  <Route path="/tahr" element={<Thar />} />
                   <Route path="/tahr-hunting-new-zealand" element={<Thar />} />
                  <Route path="/chamois" element={<Chamois />} />
                  <Route path="/chamois-hunting-new-zealand" element={<Chamois />} />
                  <Route path="/rams" element={<Rams />} />
                  <Route path="/arapawa-ram-hunting-new-zealand" element={<Rams />} />
                  <Route path="/fallow" element={<Fallow />} />
                  <Route path="/fallow-deer-hunting-new-zealand" element={<Fallow />} />
                  <Route path="/goats" element={<Goat />} />
                   <Route path="/goat-hunting-new-zealand" element={<Goat />} />
                  <Route path="/team" element={<Team />} />
                  <Route path="/professional-hunting-guide-team" element={<Team />} />
                  <Route path="/inquire" element={<Inquire />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/packages" element={<Packages />} />
                  <Route path="/outreach" element={<OutReach />} />
                </Routes>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "absolute",
                  width: "100vw",
                  bottom: "5px",
                  fontSize: ".65rem",
                  padding: ".5rem",
                  zIndex: 100,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    color: "#EA9101",
                  }}
                >
                  <AiOutlineCopyright />
                  <div
                    style={{ paddingLeft: ".25rem" }}
                  >{`NZ COUNTRY ${new Date().getUTCFullYear()}`}</div>
                </div>
              </div>
            </div>
          </div>
        </BrowserRouter>
      </ChakraProvider>
    </RecoilRoot>
  );
}

export default App;
